import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { services } from '../data/mock';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteRequest = () => {
  const { language } = useLanguage();
  const t = translations[language];
  
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    companySize: '',
    industry: '',
    services: [],
    message: '',
    timeline: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/quote`, {
        ...formData,
        language: language
      });
      
      if (response.data) {
        const successMsg = language === 'en'
          ? 'Quote request received! We will contact you within 48 hours with a personalized offer.'
          : 'Richiesta preventivo ricevuta! Ti contatteremo entro 48 ore con un\'offerta personalizzata.';
        toast.success(successMsg);
        
        // Reset form
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          companySize: '',
          industry: '',
          services: [],
          message: '',
          timeline: ''
        });
      }
    } catch (error) {
      console.error('Quote request error:', error);
      const errorMsg = language === 'en'
        ? 'Error submitting request. Please try again later.'
        : 'Errore nell\'invio della richiesta. Riprova più tardi.';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="quote-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="page-title">{t.quote.title}</h1>
          <p className="page-subtitle">
            {t.quote.subtitle}
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="quote-form-section">
        <div className="quote-form-container">
          <form onSubmit={handleSubmit} className="quote-form">
            {/* Company Information */}
            <div className="form-section">
              <h3 className="form-section-title">{t.quote.companyInfo}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="companyName" className="form-label">
                    {t.quote.companyName} *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder={t.quote.placeholders.company}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="industry" className="form-label">
                    {t.quote.industry} *
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                    className="form-select"
                  >
                    <option value="">{t.quote.selectIndustry}</option>
                    <option value="manufacturing">{t.quote.industries.manufacturing}</option>
                    <option value="construction">{t.quote.industries.construction}</option>
                    <option value="logistics">{t.quote.industries.logistics}</option>
                    <option value="services">{t.quote.industries.services}</option>
                    <option value="healthcare">{t.quote.industries.healthcare}</option>
                    <option value="food">{t.quote.industries.food}</option>
                    <option value="other">{t.quote.industries.other}</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="companySize" className="form-label">
                    {t.quote.companySize} *
                  </label>
                  <select
                    id="companySize"
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleChange}
                    required
                    className="form-select"
                  >
                    <option value="">{t.quote.selectSize}</option>
                    <option value="1-10">{t.quote.sizes['1-10']}</option>
                    <option value="11-50">{t.quote.sizes['11-50']}</option>
                    <option value="51-250">{t.quote.sizes['51-250']}</option>
                    <option value="251-500">{t.quote.sizes['251-500']}</option>
                    <option value="500+">{t.quote.sizes['500+']}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="timeline" className="form-label">
                    {t.quote.timeline} *
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    required
                    className="form-select"
                  >
                    <option value="">{t.quote.selectTimeline}</option>
                    <option value="urgent">{t.quote.timelines.urgent}</option>
                    <option value="normal">{t.quote.timelines.normal}</option>
                    <option value="flexible">{t.quote.timelines.flexible}</option>
                    <option value="exploring">{t.quote.timelines.exploring}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-section">
              <h3 className="form-section-title">{t.quote.contactInfo}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contactName" className="form-label">
                    {t.quote.name} *
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder={t.quote.placeholders.name}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    {t.quote.email} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder={t.quote.placeholders.email}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  {t.quote.phone} *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder={t.quote.placeholders.phone}
                />
              </div>
            </div>

            {/* Services Selection */}
            <div className="form-section">
              <h3 className="form-section-title">{t.quote.servicesInterest} *</h3>
              <div className="services-checkboxes">
                {services.map((service) => (
                  <label key={service.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service.id)}
                      onChange={() => handleServiceToggle(service.id)}
                      className="checkbox-input"
                    />
                    <div className="checkbox-content">
                      <CheckCircle size={20} className="checkbox-icon" />
                      <div>
                        <div className="checkbox-title">{service.title}</div>
                        <div className="checkbox-subtitle">{service.subtitle}</div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Additional Information */}
            <div className="form-section">
              <h3 className="form-section-title">{t.quote.additionalInfo}</h3>
              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  {t.quote.describe}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  className="form-textarea"
                  placeholder={language === 'en' 
                    ? 'Tell us more about your specific needs, objectives, and any other information you find useful...'
                    : 'Raccontaci di più sulle tue esigenze specifiche, obiettivi e qualsiasi altra informazione che ritieni utile...'}
                ></textarea>
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-submit">
              <button
                type="submit"
                disabled={isSubmitting || formData.services.length === 0}
                className="btn-primary btn-large"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="spinner" />
                    {t.quote.submitting}
                  </>
                ) : (
                  t.quote.submit
                )}
              </button>
              <p className="form-note">
                {t.quote.note}
              </p>
            </div>
          </form>

          {/* Info Sidebar */}
          <div className="quote-info-sidebar">
            <div className="info-card">
              <h3 className="info-title">{t.quote.whatHappens}</h3>
              <div className="info-steps">
                <div className="info-step">
                  <div className="step-number">1</div>
                  <div className="step-text">
                    <strong>{language === 'en' ? 'Request Analysis' : 'Analisi della Richiesta'}</strong>
                    <p>{language === 'en' ? 'We carefully examine your needs' : 'Esaminiamo attentamente le tue esigenze'}</p>
                  </div>
                </div>
                <div className="info-step">
                  <div className="step-number">2</div>
                  <div className="step-text">
                    <strong>{language === 'en' ? 'Personalized Quote' : 'Preventivo Personalizzato'}</strong>
                    <p>{language === 'en' ? 'We prepare a tailored offer for you' : 'Prepariamo un\'offerta su misura per te'}</p>
                  </div>
                </div>
                <div className="info-step">
                  <div className="step-number">3</div>
                  <div className="step-text">
                    <strong>{language === 'en' ? 'Free Consultation' : 'Consulenza Gratuita'}</strong>
                    <p>{language === 'en' ? 'We will contact you for an introductory call' : 'Ti contatteremo per una call conoscitiva'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="info-card">
              <h3 className="info-title">{t.quote.helpTitle}</h3>
              <p className="info-description">
                {t.quote.helpDesc}
              </p>
              <div className="contact-details">
                <div className="contact-item">
                  <strong>{t.quote.office}:</strong>
                  <a href="tel:+390187150 1655">+39 0187 150 1655</a>
                </div>
                <div className="contact-item">
                  <strong>{t.quote.sales}:</strong>
                  <a href="tel:+393457845440">+39 345 784 5440</a>
                </div>
                <div className="contact-item">
                  <strong>Email:</strong>
                  <a href="mailto:info@mallorie-consulting.it">info@mallorie-consulting.it</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuoteRequest;
